/** biome-ignore-all lint/suspicious/noArrayIndexKey: Because we need to use the index as a key */
'use client';

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import useEmblaCarousel from 'embla-carousel-react';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { cn } from '@/utils/cn';
import './style.css';

interface ArrowCarouselProps {
  slideWidthPercentage: number;
  children: React.ReactNode[];
  className?: string;
}

const getDotSizeClass = (distance: number) => {
  if (distance === 0) {
    return 'w-2.5 h-2.5';
  }
  if (distance === 1) {
    return 'w-2 h-2';
  }
  if (distance === 2) {
    return 'w-1.5 h-1.5';
  }
  return 'w-1 h-1';
};

export const ArrowCarousel: React.FC<ArrowCarouselProps> = ({
  slideWidthPercentage,
  children,
  className,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    loop: true,
    slidesToScroll: 1,
    dragFree: false,
  });

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev();
    }
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext();
    }
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) {
      return;
    }
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    const handleReInit = () => {
      setScrollSnaps(emblaApi.scrollSnapList());
      onSelect();
    };

    onSelect();
    setScrollSnaps(emblaApi.scrollSnapList());

    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', handleReInit);
  }, [emblaApi, onSelect]);

  return (
    <div className={cn('relative', className)}>
      <ArrowButton
        direction="prev"
        isDisabled={!canScrollPrev}
        onClick={scrollPrev}
      />

      <section
        className="arrow-carousel-embla"
        style={
          {
            '--slide-size': `${slideWidthPercentage}%`,
          } as React.CSSProperties
        }
      >
        <div className="arrow-carousel-embla__viewport" ref={emblaRef}>
          <div className="arrow-carousel-embla__container">
            {children.map((child, index) => (
              <div className="arrow-carousel-embla__slide" key={index}>
                {child}
              </div>
            ))}
          </div>
        </div>
      </section>

      <ArrowButton
        direction="next"
        isDisabled={!canScrollNext}
        onClick={scrollNext}
      />

      <div className="lg:hidden">
        <div className="-mx-1 mt-4 flex h-2.5 items-center justify-center">
          {scrollSnaps.map((_, index) => {
            const slideCount = scrollSnaps.length;
            const diff = Math.abs(selectedIndex - index);
            const distance = Math.min(diff, slideCount - diff);

            const sizeClass = getDotSizeClass(distance);

            return (
              <div
                className={cn(
                  'mx-1 rounded-full transition-all duration-300',
                  sizeClass,
                  index === selectedIndex
                    ? 'bg-light-blue'
                    : 'bg-white/20 hover:bg-white/40'
                )}
                key={index}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

type ArrowDirection = 'prev' | 'next';

function ArrowButton({
  direction,
  onClick,
  isDisabled,
}: {
  direction: ArrowDirection;
  onClick: () => void;
  isDisabled: boolean;
}) {
  const arrowIconClass = 'w-4 h-4 lg:w-5 lg:h-5 text-light-blue stroke-2';

  const buttonDirectionProps: Record<
    ArrowDirection,
    {
      className: string;
      icon: React.ReactNode;
      ariaLabel: string;
    }
  > = {
    prev: {
      className: '-left-6 lg:-left-8 xl:-left-12',
      icon: <ChevronLeftIcon className={arrowIconClass} strokeWidth={2} />,
      ariaLabel: 'Previous slide',
    },
    next: {
      className: '-right-6 lg:-right-8 xl:-right-12',
      icon: <ChevronRightIcon className={arrowIconClass} strokeWidth={2} />,
      ariaLabel: 'Next slide',
    },
  };

  const buttonProps = buttonDirectionProps[direction];

  return (
    <button
      aria-label={buttonProps.ariaLabel}
      className={cn(
        '-translate-y-1/2 absolute top-1/2 z-10 rounded-full border border-white/20 bg-black/60 p-1.5 transition-all duration-200 hover:bg-black/80 lg:p-2',
        'hidden lg:block',
        buttonProps.className,
        isDisabled && 'cursor-not-allowed opacity-30'
      )}
      disabled={isDisabled}
      onClick={onClick}
      type="button"
    >
      {buttonProps.icon}
    </button>
  );
}
